import { randomUuid } from '@lemonade-hq/ts-helpers';
import type { IconName, IconVariant } from '@lemonade-hq/uniclient-theme';
import type { UniclientAction } from '../action';
import type { FormattedText } from '../utils';
import type { FormElementProps } from './form/form.types';
import type { IconTextList } from './icon_text_list';
import type { PriceValueListItems } from './price_value_list';
import type { UniclientBaseComponentProps, UniclientWizardComponentBase } from './types';

interface ProductCardIcon {
  readonly name: IconName;
  readonly variant?: IconVariant;
}

type BaseProductCardHeaderProps = {
  readonly icon?: ProductCardIcon;
  readonly title: FormattedText;
  readonly illustration?: string;
};

interface ProductCardHeaderWithSubtitle {
  readonly subtitle: FormattedText;
  readonly additionalText?: FormattedText;
  readonly price?: never;
  readonly previousPrice?: never;
}

interface ProductCardHeaderWithPrice {
  readonly subtitle?: never;
  readonly additionalText?: never;
  readonly price: FormattedText;
  readonly previousPrice?: FormattedText;
}

export type ProductCardHeaderProps = BaseProductCardHeaderProps &
  (ProductCardHeaderWithPrice | ProductCardHeaderWithSubtitle);

export enum ProductCardContentItemsType {
  IconTextList = 'icon_text_list',
  FormattedText = 'formatted_text',
  PriceValueList = 'price_value_list',
}

export type ProductCardContentItemsIconTextList = {
  readonly type: ProductCardContentItemsType.IconTextList;
  readonly content: IconTextList;
};

export type ProductCardContentItemsFormattedText = {
  readonly type: ProductCardContentItemsType.FormattedText;
  readonly content: FormattedText;
};

export type ProductCardContentItemsPriceValueList = {
  readonly type: ProductCardContentItemsType.PriceValueList;
  readonly content: PriceValueListItems;
};

export type ProductCartMixedContentItems = (
  | ProductCardContentItemsFormattedText
  | ProductCardContentItemsIconTextList
  | ProductCardContentItemsPriceValueList
)[];
export interface ProductCardContentItemsProps {
  readonly items: IconTextList | ProductCartMixedContentItems;
}

interface BaseProductCardContentProps extends ProductCardContentItemsProps {
  readonly title?: FormattedText;
  readonly subtitle?: string;
  readonly actions?: UniclientAction | UniclientAction[];
}

export interface ProductCardAccordionContentProps extends BaseProductCardContentProps {
  readonly accordionId: string;
  readonly accordion: true;
  readonly initiallyClosed?: boolean;
}

interface ProductCardPlainContentProps extends BaseProductCardContentProps {
  readonly accordion?: false;
  readonly initiallyClosed?: never;
  readonly accordionId?: never;
}

export type ProductCardContentProps = ProductCardAccordionContentProps | ProductCardPlainContentProps;

export interface ProductCardProps<TAttribute extends string = string> extends UniclientBaseComponentProps {
  readonly header: ProductCardHeaderProps;
  readonly bottomInput?: FormElementProps<TAttribute>;
  readonly action?: UniclientAction;
  readonly content?: ProductCardContentProps;
}

export type UniclientProductCardProps = ProductCardProps & UniclientWizardComponentBase<'product_card'>;

export function productCard(props: ProductCardProps | ProductCardProps['header']): UniclientProductCardProps {
  if ('header' in props) {
    return { ...props, type: 'product_card' };
  }

  return { header: props, type: 'product_card' };
}

export function productCardAccordionContent(
  props: Omit<ProductCardAccordionContentProps, 'accordion' | 'accordionId'>,
): ProductCardAccordionContentProps {
  return { accordion: true, ...props, accordionId: randomUuid() };
}

export function productCardIconTextList(
  content: ProductCardContentItemsIconTextList['content'],
): ProductCardContentItemsIconTextList {
  return { type: ProductCardContentItemsType.IconTextList, content };
}

export function productCardFormattedText(
  content: ProductCardContentItemsFormattedText['content'],
): ProductCardContentItemsFormattedText {
  return { type: ProductCardContentItemsType.FormattedText, content };
}

export function productCardPriceValueList(
  content: ProductCardContentItemsPriceValueList['content'],
): ProductCardContentItemsPriceValueList {
  return { type: ProductCardContentItemsType.PriceValueList, content };
}
